import { Box, Button, makeStyles, TextField, Typography } from '@material-ui/core'
import { DatePicker } from '@mui/x-date-pickers'
import axios from 'axios'
import moment from 'moment'
import MUIDataTable from 'mui-datatables'
import React, { useEffect, useRef, useState } from 'react'
import ReservationTableLabels from './ReservationTableLabels'
import formatVehicleLabel from './formatVehicleLabel'
import useReservationClock from './reservationLifecycle'

const useStyles = makeStyles((theme) => ({
  section: { marginTop: theme.spacing(3) },
  filters: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    margin: theme.spacing(1, 0),
    '& .MuiTextField-root': { width: 200 },
    '& .MuiOutlinedInput-notchedOutline': { borderBottom: '1px solid rgba(0, 0, 0, 0.23)' }
  },
  action: {
    '&.Mui-disabled': { opacity: 0.5 }
  },
  leave: { color: theme.palette.error.main, borderColor: theme.palette.error.main }
}))

const UpcomingReservationsGrid = ({ currentUserId, reloadKey, onChanged }) => {
  const classes = useStyles()
  const [reservations, setReservations] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [reload, setReload] = useState(0)
  const [departureDate, setDepartureDate] = useState(null)
  const [destination, setDestination] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [busyId, setBusyId] = useState(null)
  const busy = useRef(false)
  const now = useReservationClock(reservations, () => setReload((previous) => previous + 1))

  useEffect(() => {
    let active = true
    const request = axios.CancelToken.source()
    setLoading(true)
    setLoadError('')
    axios
      .get('vehicle-reservations/upcoming', { cancelToken: request.token })
      .then(({ data }) => {
        if (!data.success || !Array.isArray(data.data)) throw new Error('Reservations unavailable')
        if (active) setReservations(data.data)
      })
      .catch((error) => {
        if (active) {
          setReservations([])
          setLoadError(error.response?.data?.message || 'Udhëtimet e ardhshme nuk u ngarkuan. Provoni përsëri.')
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
      request.cancel('Upcoming reservations request superseded or closed')
    }
  }, [reloadKey, reload])

  const changeParticipation = async (reservation, joining) => {
    if (busy.current) return
    busy.current = true
    setBusyId(reservation._id)
    setActionError('')
    try {
      const { data } = await axios.post(`vehicle-reservations/${reservation._id}/${joining ? 'join-with-group' : 'leave'}`)
      if (!data.success || !data.data) throw new Error('Participation unavailable')
      setReservations((previous) => previous.map((item) => (item._id === data.data._id ? data.data : item)))
      onChanged()
    } catch (error) {
      setActionError(error.response?.data?.message || 'Veprimi nuk u ruajt. Provoni përsëri.')
      if ([404, 409].includes(error.response?.status)) {
        setReload((previous) => previous + 1)
        onChanged()
      }
    } finally {
      busy.current = false
      setBusyId(null)
    }
  }

  const filtered = reservations.filter(
    (reservation) =>
      new Date(reservation.departureAt).getTime() > now &&
      (!departureDate || !moment(departureDate).isValid() || moment(reservation.departureDate).isSame(departureDate, 'day')) &&
      reservation.destination.toLocaleLowerCase().includes(destination.trim().toLocaleLowerCase())
  )
  const columns = [
    { name: 'ownerName', label: 'Rezervuesi', options: { sort: false, customBodyRender: (value) => value || '—' } },
    {
      name: 'departureDate',
      label: 'Data e nisjes',
      options: { sort: false, customBodyRender: (value) => moment(value).format('DD/MM/YYYY') }
    },
    { name: 'departureTime', label: 'Ora e nisjes', options: { sort: false } },
    {
      name: 'returnDate',
      label: 'Data e kthimit',
      options: { sort: false, customBodyRender: (value) => moment(value).format('DD/MM/YYYY') }
    },
    { name: 'returnTime', label: 'Ora e kthimit', options: { sort: false } },
    {
      name: 'vehicles',
      label: 'Automjetet',
      options: { sort: false, customBodyRender: (value) => value.map(formatVehicleLabel).join(', ') }
    },
    { name: 'destination', label: 'Destinacioni', options: { sort: false } },
    { name: 'availableSeats', label: 'Vende të lira', options: { sort: false } },
    {
      name: '_id',
      label: 'Veprimet',
      options: {
        sort: false,
        customBodyRender: (id) => {
          const reservation = reservations.find((item) => item._id === id)
          if (reservation.owner === currentUserId)
            return (
              <Typography variant='body2' color='textSecondary'>
                Rezervimi juaj
              </Typography>
            )
          const joined = reservation.participants.some((person) => person._id === currentUserId)
          const needsVehicle = !joined && reservation.availableSeats === 0
          return (
            <Button
              variant='outlined'
              color={joined ? 'default' : 'primary'}
              size='small'
              className={`${classes.action} ${joined ? classes.leave : ''}`}
              disabled={!currentUserId || loading || Boolean(loadError) || Boolean(busyId) || (!joined && !reservation.canJoin)}
              onClick={() => changeParticipation(reservation, !joined)}
              title={!joined && !reservation.canJoin ? reservation.joinUnavailableReason : ''}
              aria-label={`${joined ? 'Largohu nga' : needsVehicle ? 'Shto automjet dhe bashkohu në' : 'Bashkohu në'} udhëtimi për ${
                reservation.destination
              }`}
            >
              {busyId === id ? 'Duke ruajtur…' : joined ? 'Largohu' : needsVehicle ? 'Shto automjet dhe bashkohu' : 'Bashkohu'}
            </Button>
          )
        }
      }
    }
  ]
  const options = {
    download: false,
    print: false,
    filter: false,
    search: false,
    viewColumns: false,
    selectableRows: 'none',
    responsive: 'scroll',
    elevation: 4,
    customToolbar: () => (
      <Box className={classes.filters}>
        <DatePicker
          label='Data e nisjes'
          inputFormat='dd/MM/yyyy'
          value={departureDate}
          onChange={(value) => {
            setDepartureDate(value)
            setPage(0)
          }}
          componentsProps={{ actionBar: { actions: ['clear'] } }}
          renderInput={(params) => <TextField {...params} id='upcoming-departure-date' variant='outlined' size='small' />}
        />
        <TextField
          id='upcoming-destination'
          label='Destinacioni'
          variant='outlined'
          size='small'
          value={destination}
          onChange={(event) => {
            setDestination(event.target.value)
            setPage(0)
          }}
        />
        <Button disabled={Boolean(busyId)} onClick={() => setReload((previous) => previous + 1)}>
          Refresh
        </Button>
      </Box>
    ),
    page,
    rowsPerPage,
    rowsPerPageOptions: [10, 50, 100, 200],
    onChangePage: setPage,
    onChangeRowsPerPage: (value) => {
      setRowsPerPage(value)
      setPage(0)
    },
    textLabels: new ReservationTableLabels(
      loading ? 'Duke ngarkuar udhëtimet…' : loadError ? 'Udhëtimet nuk janë ngarkuar.' : 'Nuk ka udhëtime të ardhshme'
    )
  }

  return (
    <Box className={classes.section} data-testid='upcoming-reservations'>
      {(loadError || actionError) && (
        <Typography role='alert' color='error'>
          {loadError || actionError}
        </Typography>
      )}
      <MUIDataTable title='Udhëtimet e ardhshme' data={filtered} columns={columns} options={options} />
    </Box>
  )
}

export default UpcomingReservationsGrid
